import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Loader2,
  Calendar,
  Check,
  X,
  XCircle,
  Clock,
  Mail,
  Phone,
  Users
} from "lucide-react";
import { getReservations, updateReservationStatut, getLogements } from "@/lib/api";

const statusColors = {
  en_attente: "bg-yellow-100 text-yellow-700",
  confirmee: "bg-green-100 text-green-700",
  refusee: "bg-red-100 text-red-700",
  annulee: "bg-gray-100 text-gray-700"
};

const statusLabels = {
  en_attente: "En attente",
  confirmee: "Confirmée",
  refusee: "Refusée",
  annulee: "Annulée"
};

export default function ReservationsAdminPage() {
  const [reservations, setReservations] = useState([]);
  const [logements, setLogements] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [resRes, logRes] = await Promise.all([
        getReservations(),
        getLogements()
      ]);
      setReservations(resRes.data);
      
      const logMap = {};
      logRes.data.forEach(l => logMap[l.id] = l);
      setLogements(logMap);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateReservationStatut(id, newStatus);
      toast.success(`Réservation ${statusLabels[newStatus].toLowerCase()} !`);
      loadData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const filteredReservations = filter 
    ? reservations.filter(r => r.statut === filter)
    : reservations;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="reservations-admin-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-display text-[#004333]">Réservations</h2>
          <p className="text-gray-500">{reservations.length} réservation(s)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("")}
            className={`px-4 py-2 text-sm ${!filter ? "bg-[#004333] text-white" : "bg-gray-100 text-gray-600"}`}
          >
            Toutes
          </button>
          <button
            onClick={() => setFilter("en_attente")}
            className={`px-4 py-2 text-sm ${filter === "en_attente" ? "bg-yellow-500 text-white" : "bg-gray-100 text-gray-600"}`}
          >
            En attente
          </button>
          <button
            onClick={() => setFilter("confirmee")}
            className={`px-4 py-2 text-sm ${filter === "confirmee" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600"}`}
          >
            Confirmées
          </button>
          <button
            onClick={() => setFilter("annulee")}
            className={`px-4 py-2 text-sm ${filter === "annulee" ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600"}`}
          >
            Annulées
          </button>
        </div>
      </div>

      {filteredReservations.length > 0 ? (
        <div className="space-y-4">
          {filteredReservations.map((reservation) => {
            const logement = logements[reservation.logement_id];
            return (
              <div key={reservation.id} className="bg-white border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-display text-lg text-[#004333]">
                      {logement?.titre || "Logement inconnu"}
                    </h3>
                    <span className={`inline-block mt-1 px-3 py-1 text-xs font-medium ${statusColors[reservation.statut]}`}>
                      {statusLabels[reservation.statut]}
                    </span>
                  </div>
                  <div className="font-mono text-xl font-bold text-[#004333]">
                    {reservation.montant_total} €
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-2">Voyageur</h4>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Users className="w-4 h-4 text-[#C9922A]" />
                        {reservation.voyageur_nom} ({reservation.nb_personnes} pers.)
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Mail className="w-4 h-4" />
                        {reservation.voyageur_email}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Phone className="w-4 h-4" />
                        {reservation.voyageur_telephone}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-2">Séjour</h4>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Calendar className="w-4 h-4 text-[#C9922A]" />
                      {reservation.date_arrivee} → {reservation.date_depart}
                    </div>
                    {reservation.message && (
                      <div className="mt-2 p-3 bg-gray-50 text-sm text-gray-600">
                        "{reservation.message}"
                      </div>
                    )}
                  </div>
                </div>
                
                {reservation.statut === "en_attente" && (
                  <div className="flex gap-4 mt-6 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleStatusChange(reservation.id, "confirmee")}
                      className="flex-1 btn-primary flex items-center justify-center gap-2"
                      data-testid={`confirm-${reservation.id}`}
                    >
                      <Check className="w-4 h-4" />
                      Confirmer
                    </button>
                    <button
                      onClick={() => handleStatusChange(reservation.id, "refusee")}
                      className="flex-1 btn-secondary flex items-center justify-center gap-2 hover:bg-red-50 hover:border-red-500 hover:text-red-500"
                      data-testid={`refuse-${reservation.id}`}
                    >
                      <X className="w-4 h-4" />
                      Refuser
                    </button>
                  </div>
                )}
                
                {reservation.statut === "confirmee" && (
                  <div className="flex gap-4 mt-6 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => {
                        if (window.confirm("Êtes-vous sûr de vouloir annuler cette réservation ? Les dates seront à nouveau disponibles.")) {
                          handleStatusChange(reservation.id, "annulee");
                        }
                      }}
                      className="flex-1 bg-red-50 text-red-600 border border-red-200 px-4 py-3 font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                      data-testid={`cancel-${reservation.id}`}
                    >
                      <XCircle className="w-4 h-4" />
                      Annuler la réservation
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-16 text-center">
          <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-[#004333] mb-2">Aucune réservation</h3>
          <p className="text-gray-500">Les demandes de réservation apparaîtront ici.</p>
        </div>
      )}
    </div>
  );
}
